import { useEffect, useState, type FormEvent } from "react";
import {
  isoToTenantDateTimeInputValue,
  tenantDateTimeInputValueToIso,
  type AdminPublicEventDto,
  type AdminPublicEventsResponseDto,
  type PublicAttachmentType
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { getEnv } from "../../shared/lib/env";
import {
  completeAdminPublicEventAttachmentUpload,
  createAdminPublicEvent,
  createAdminPublicEventAttachment,
  createAdminPublicEventAttachmentUploadSession,
  fetchAdminPublicEvent,
  fetchAdminPublicEvents,
  hideAdminPublicEventAttachment,
  patchAdminPublicEvent,
  republishAdminPublicEventAttachment
} from "../../shared/api/public";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const env = getEnv();
const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/public-settings", label: "公開設定" }
];
const SUPPORTED_ATTACHMENT_ACCEPT =
  "image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/quicktime";

type EventFormState = {
  title: string;
  summary: string;
  body: string;
  publicStatus: "draft" | "published" | "hidden";
  publishFrom: string;
  publishTo: string;
  sortOrder: string;
};

type AttachmentFormState = {
  displayName: string;
  selectedFile: File | null;
};

function createEmptyForm(): EventFormState {
  return {
    title: "",
    summary: "",
    body: "",
    publicStatus: "draft",
    publishFrom: "",
    publishTo: "",
    sortOrder: "0"
  };
}

function createFormFromEvent(event: AdminPublicEventDto): EventFormState {
  return {
    title: event.title,
    summary: event.summary ?? "",
    body: event.body ?? "",
    publicStatus: event.publicStatus,
    publishFrom: isoToTenantDateTimeInputValue(event.publishFrom, event.timeZone),
    publishTo: isoToTenantDateTimeInputValue(event.publishTo, event.timeZone),
    sortOrder: String(event.sortOrder)
  };
}

function createEmptyAttachmentForm(): AttachmentFormState {
  return {
    displayName: "",
    selectedFile: null
  };
}

function resolveAttachmentType(file: File): PublicAttachmentType | null {
  if (file.type.startsWith("image/")) {
    return file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp" ? "image" : null;
  }

  if (file.type === "application/pdf") {
    return "pdf";
  }

  if (file.type === "video/mp4" || file.type === "video/quicktime") {
    return "video";
  }

  return null;
}

function isLikelyUploadNetworkError(error: unknown): boolean {
  return error instanceof Error
    && (error.message === "Load failed"
      || error.message === "Failed to fetch"
      || error.message.includes("Network request failed"));
}

async function uploadFileToSession(uploadUrl: string, file: File): Promise<void> {
  if (env.enableMock && uploadUrl.includes("upload.mock.local")) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", uploadUrl);
    xhr.responseType = "text";
    xhr.timeout = 120_000;
    xhr.withCredentials = false;
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }

      reject(
        new Error(
          `PUT upload failed (${xhr.status} ${xhr.statusText})${xhr.responseText ? `: ${String(xhr.responseText).slice(0, 200)}` : ""}`
        )
      );
    };

    xhr.onerror = () => reject(new Error("Load failed"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.send(file);
  });
}

function getAttachmentErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof ApiError) {
    if (error.code === "UNSUPPORTED_PUBLIC_EVENT_ATTACHMENT_MIME_TYPE") {
      return "対応している画像・PDF・動画ファイルを選択してください。";
    }

    if (error.code === "PUBLIC_EVENT_ATTACHMENT_TOO_LARGE") {
      return "ファイルサイズが上限を超えています。別のファイルを選択してください。";
    }

    if (error.code === "UPLOAD_SESSION_REQUIRED" || error.code === "UPLOAD_SESSION_EXPIRED") {
      return "アップロードセッションの有効期限が切れました。もう一度お試しください。";
    }
  }

  if (isLikelyUploadNetworkError(error)) {
    return "ファイルのアップロードに失敗しました。通信状態を確認して再度お試しください。";
  }

  return getProtectedApiErrorMessage(error, fallbackMessage);
}

export function AdminPublicEventsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [eventsResponse, setEventsResponse] = useState<AdminPublicEventsResponseDto | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AdminPublicEventDto | null>(null);
  const [formState, setFormState] = useState<EventFormState>(createEmptyForm);
  const [attachmentFormState, setAttachmentFormState] = useState<AttachmentFormState>(createEmptyAttachmentForm);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  const [attachmentState, setAttachmentState] = useState<"idle" | "uploading">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const timeZone = eventsResponse?.timeZone ?? "Asia/Tokyo";

  async function loadEvents() {
    const nextEvents = await fetchAdminPublicEvents(tenantSlug);
    setEventsResponse(nextEvents);
    return nextEvents;
  }

  async function refreshSelectedEvent(eventId: string) {
    const nextEvent = await fetchAdminPublicEvent(tenantSlug, eventId);
    setSelectedEvent(nextEvent);
    setFormState(createFormFromEvent(nextEvent));
    return nextEvent;
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadInitial() {
      setLoadState("loading");
      setMessage(null);

      try {
        const nextEvents = await loadEvents();

        if (cancelled) {
          return;
        }

        setEventsResponse(nextEvents);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setMessage(getProtectedApiErrorMessage(error, "公開イベントの取得に失敗しました"));
      }
    }

    void loadInitial();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  async function handleSelectEvent(eventId: string) {
    setMessage(null);

    try {
      await refreshSelectedEvent(eventId);
      setAttachmentFormState(createEmptyAttachmentForm());
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMessage(getProtectedApiErrorMessage(error, "公開イベント詳細の取得に失敗しました"));
    }
  }

  function handleCreateNew() {
    setSelectedEvent(null);
    setFormState(createEmptyForm());
    setAttachmentFormState(createEmptyAttachmentForm());
    setMessage(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaveState("saving");
    setMessage(null);

    try {
      const request = {
        title: formState.title,
        summary: formState.summary || null,
        body: formState.body || null,
        publicStatus: formState.publicStatus,
        publishFrom: tenantDateTimeInputValueToIso(formState.publishFrom, timeZone),
        publishTo: tenantDateTimeInputValueToIso(formState.publishTo, timeZone),
        sortOrder: Number.parseInt(formState.sortOrder, 10) || 0,
        attachments: selectedEvent?.attachments ?? []
      } as const;

      const savedEvent = selectedEvent
        ? await patchAdminPublicEvent(tenantSlug, selectedEvent.eventId, request)
        : await createAdminPublicEvent(tenantSlug, request);
      await loadEvents();
      setSelectedEvent(savedEvent);
      setFormState(createFormFromEvent(savedEvent));
      setMessage(selectedEvent ? "公開イベントを更新しました。" : "公開イベントを作成しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMessage(getProtectedApiErrorMessage(error, "公開イベントの保存に失敗しました"));
    } finally {
      setSaveState("idle");
    }
  }

  async function handleUploadAttachment() {
    if (!selectedEvent) {
      setMessage("先に公開イベントを保存してください。");
      return;
    }

    if (!attachmentFormState.selectedFile) {
      setMessage("添付ファイルを選択してください。");
      return;
    }

    const attachmentType = resolveAttachmentType(attachmentFormState.selectedFile);

    if (!attachmentType) {
      setMessage("対応している画像・PDF・動画ファイルを選択してください。");
      return;
    }

    setAttachmentState("uploading");
    setMessage(null);

    try {
      const createdEvent = await createAdminPublicEventAttachment(tenantSlug, selectedEvent.eventId, {
        attachmentType,
        displayName: attachmentFormState.displayName.trim() || attachmentFormState.selectedFile.name,
        mimeType: attachmentFormState.selectedFile.type || null,
        sortOrder: selectedEvent.attachments.length
      });
      const createdAttachment = createdEvent.attachments.at(-1);

      if (!createdAttachment) {
        throw new Error("Attachment draft was not created");
      }

      const uploadSession = await createAdminPublicEventAttachmentUploadSession(
        tenantSlug,
        selectedEvent.eventId,
        createdAttachment.attachmentId
      );

      try {
        await uploadFileToSession(uploadSession.uploadUrl, attachmentFormState.selectedFile);
      } catch (error) {
        if (!isLikelyUploadNetworkError(error)) {
          throw error;
        }
      }

      await completeAdminPublicEventAttachmentUpload(
        tenantSlug,
        selectedEvent.eventId,
        createdAttachment.attachmentId,
        {
          uploadSessionToken: uploadSession.uploadSessionToken,
          originalFileName: attachmentFormState.selectedFile.name,
          mimeType: attachmentFormState.selectedFile.type || "application/octet-stream",
          fileSizeBytes: attachmentFormState.selectedFile.size
        }
      );

      await refreshSelectedEvent(selectedEvent.eventId);
      await loadEvents();
      setAttachmentFormState(createEmptyAttachmentForm());
      setMessage("添付ファイルをアップロードしました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMessage(getAttachmentErrorMessage(error, "添付ファイルのアップロードに失敗しました。"));
    } finally {
      setAttachmentState("idle");
    }
  }

  async function handleAttachmentVisibility(attachmentId: string, action: "hide" | "republish") {
    if (!selectedEvent) {
      return;
    }

    setMessage(null);

    try {
      if (action === "hide") {
        await hideAdminPublicEventAttachment(tenantSlug, selectedEvent.eventId, attachmentId);
      } else {
        await republishAdminPublicEventAttachment(tenantSlug, selectedEvent.eventId, attachmentId);
      }

      await refreshSelectedEvent(selectedEvent.eventId);
      await loadEvents();
      setMessage(action === "hide" ? "添付ファイルを非公開にしました。" : "添付ファイルを再公開しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMessage(getAttachmentErrorMessage(error, "添付ファイルの更新に失敗しました。"));
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="公開イベント"
      description="公開イベントの作成と編集を行います。"
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p style={{ margin: 0 }}>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p style={{ margin: 0 }}>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p style={{ margin: 0 }}>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p style={{ margin: 0 }}>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p style={{ margin: 0 }}>公開イベントを読み込んでいます。</p> : null}
        {message ? <p style={{ margin: 0 }}>{message}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>イベント一覧</h2>
                <button type="button" onClick={handleCreateNew}>新規イベント</button>
              </div>
              {eventsResponse?.items.length === 0 ? <p style={{ margin: 0 }}>公開イベントはまだありません。</p> : null}
              {eventsResponse?.items.map((item) => (
                <button
                  key={item.eventId}
                  type="button"
                  onClick={() => void handleSelectEvent(item.eventId)}
                  style={{
                    textAlign: "left",
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    background: selectedEvent?.eventId === item.eventId ? "#f7fbff" : "#fff"
                  }}
                >
                  <strong>{item.title}</strong>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>status: {item.publicStatus}</div>
                  {item.summary ? <div style={{ fontSize: 14, opacity: 0.85 }}>{item.summary}</div> : null}
                </button>
              ))}
            </section>

            <form onSubmit={(event) => void handleSubmit(event)} style={{ display: "grid", gap: 14 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{selectedEvent ? "イベント編集" : "イベント作成"}</h2>

              <label style={{ display: "grid", gap: 6 }}>
                <span>タイトル</span>
                <input
                  value={formState.title}
                  onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span>概要</span>
                <textarea
                  value={formState.summary}
                  onChange={(event) => setFormState((current) => ({ ...current, summary: event.target.value }))}
                  rows={3}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span>本文</span>
                <textarea
                  value={formState.body}
                  onChange={(event) => setFormState((current) => ({ ...current, body: event.target.value }))}
                  rows={8}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span>公開状態</span>
                <select
                  value={formState.publicStatus}
                  onChange={(event) => setFormState((current) => ({
                    ...current,
                    publicStatus: event.target.value as EventFormState["publicStatus"]
                  }))}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                  <option value="hidden">hidden</option>
                </select>
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span>公開開始</span>
                <input
                  type="datetime-local"
                  value={formState.publishFrom}
                  onChange={(event) => setFormState((current) => ({ ...current, publishFrom: event.target.value }))}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span>公開終了</span>
                <input
                  type="datetime-local"
                  value={formState.publishTo}
                  onChange={(event) => setFormState((current) => ({ ...current, publishTo: event.target.value }))}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span>表示順</span>
                <input
                  type="number"
                  value={formState.sortOrder}
                  onChange={(event) => setFormState((current) => ({ ...current, sortOrder: event.target.value }))}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <section style={{ display: "grid", gap: 10 }}>
                <span>添付ファイル</span>
                {selectedEvent ? (
                  <>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span>表示名</span>
                      <input
                        value={attachmentFormState.displayName}
                        onChange={(event) => setAttachmentFormState((current) => ({
                          ...current,
                          displayName: event.target.value
                        }))}
                        placeholder="未入力ならファイル名を使います。"
                        style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                      />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span>画像 / PDF / 動画</span>
                      <input
                        type="file"
                        accept={SUPPORTED_ATTACHMENT_ACCEPT}
                        onChange={(event) => setAttachmentFormState((current) => ({
                          ...current,
                          selectedFile: event.target.files?.[0] ?? null
                        }))}
                      />
                    </label>
                    {attachmentFormState.selectedFile ? (
                      <div style={{ fontSize: 13, opacity: 0.8 }}>
                        選択中: {attachmentFormState.selectedFile.name}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void handleUploadAttachment()}
                      disabled={attachmentState === "uploading"}
                    >
                      {attachmentState === "uploading" ? "アップロード中..." : "添付をアップロード"}
                    </button>
                    {selectedEvent.attachments.length ? (
                      selectedEvent.attachments.map((attachment) => (
                        <article
                          key={attachment.attachmentId}
                          style={{
                            display: "grid",
                            gap: 6,
                            padding: 12,
                            borderRadius: 12,
                            border: "1px solid #e5e7eb"
                          }}
                        >
                          <strong>{attachment.displayName}</strong>
                          <div style={{ fontSize: 13, opacity: 0.8 }}>
                            {attachment.attachmentType} / upload:{attachment.uploadStatus} / publish:{attachment.publishStatus}
                          </div>
                          {attachment.mimeType ? <div style={{ fontSize: 13, opacity: 0.8 }}>{attachment.mimeType}</div> : null}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => void handleAttachmentVisibility(attachment.attachmentId, "hide")}>
                              非公開
                            </button>
                            <button type="button" onClick={() => void handleAttachmentVisibility(attachment.attachmentId, "republish")}>
                              再公開
                            </button>
                          </div>
                        </article>
                      ))
                    ) : (
                      <div style={{ fontSize: 14, opacity: 0.8 }}>添付はありません。</div>
                    )}
                    <div style={{ fontSize: 13, opacity: 0.8 }}>
                      画像/PDF は 20MB、動画は 500MB までです。公開中イベントに complete すると自動で公開添付に入ります。
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 14, opacity: 0.8 }}>先にイベントを作成すると添付ファイルを追加できます。</div>
                )}
              </section>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="submit" disabled={saveState === "saving"}>
                  {saveState === "saving" ? "保存中..." : selectedEvent ? "更新" : "作成"}
                </button>
                <Link to={`/t/${tenantSlug}/public/events`}>公開ページを確認</Link>
              </div>
            </form>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
